import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { Heart, MessageCircle, X, MapPin, ChevronLeft, ChevronRight, Bookmark, Send } from "lucide-react";
import { FL } from "@/constants/testIds/fairylights";

export default function PostGrid({ posts }) {
  const [activeIndex, setActiveIndex] = useState(null);

  const open = (i) => setActiveIndex(i);
  const close = () => setActiveIndex(null);
  const next = () => setActiveIndex((i) => (i === null ? null : (i + 1) % posts.length));
  const prev = () => setActiveIndex((i) => (i === null ? null : (i - 1 + posts.length) % posts.length));

  return (
    <section id="work" className="relative bg-[#4A0E1F] px-6 pt-8 sm:px-10 md:px-14">
      <div className="mx-auto max-w-5xl">
        <div
          data-testid={FL.postGrid}
          className="grid grid-cols-3 gap-1 sm:gap-1.5"
        >
          {posts.map((p, i) => (
            <motion.button
              key={p.id}
              data-testid={FL.post(p.id)}
              onClick={() => open(i)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                delay: (i % 9) * 0.04,
                duration: 0.7,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="grid-item aspect-square"
              aria-label={`Open post ${p.id}`}
            >
              <img
                src={p.image}
                alt={p.caption}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center gap-5 bg-black/45 opacity-0 transition-opacity duration-500 hover:opacity-100">
                <span className="flex items-center gap-1.5 font-body text-sm text-[#FDFBF7]">
                  <Heart className="h-4 w-4 fill-[#FDFBF7]" />
                  {formatK(p.likes)}
                </span>
                <span className="flex items-center gap-1.5 font-body text-sm text-[#FDFBF7]">
                  <MessageCircle className="h-4 w-4 fill-[#FDFBF7]" />
                  {p.comments}
                </span>
              </div>
            </motion.button>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start gap-3 border-t border-[#D4AF37]/25 pt-8 text-[#FDFBF7]/60 sm:flex-row sm:items-center sm:justify-between">
          <span className="font-italic-serif text-lg">
            End of the archive — 248 chapters, and counting.
          </span>
          <span className="font-body text-[11px] uppercase tracking-[0.28em]">
            More on Instagram → @fairylights.uae
          </span>
        </div>
      </div>

      <AnimatePresence>
        {activeIndex !== null && (
          <PostModal
            post={posts[activeIndex]}
            onClose={close}
            onNext={next}
            onPrev={prev}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function formatK(n) {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return n;
}

function PostModal({ post, onClose, onNext, onPrev }) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrev();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose, onNext, onPrev]);

  return (
    <motion.div
      data-testid={FL.postModal}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-[#1A050B]/95 backdrop-blur-md p-4"
      onClick={onClose}
    >
      <button
        data-testid={FL.postPrev}
        onClick={(e) => {
          e.stopPropagation();
          onPrev();
        }}
        className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full border border-[#D4AF37]/40 p-2 text-[#FDFBF7] transition-colors hover:bg-white/10 md:left-8"
        aria-label="Previous post"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        data-testid={FL.postNext}
        onClick={(e) => {
          e.stopPropagation();
          onNext();
        }}
        className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full border border-[#D4AF37]/40 p-2 text-[#FDFBF7] transition-colors hover:bg-white/10 md:right-8"
        aria-label="Next post"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <motion.div
        key={post.id}
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 12 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative grid w-full max-w-5xl overflow-hidden rounded-md border border-[#D4AF37]/25 bg-[#1A050B] md:grid-cols-[1.2fr_1fr]"
      >
        <button
          data-testid={FL.postModalClose}
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-black/30 p-1.5 text-[#FDFBF7] backdrop-blur-sm transition-colors hover:bg-black/60 md:hidden"
          aria-label="Close post"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative bg-black">
          <img
            src={post.image}
            alt={post.caption}
            className="h-full w-full object-cover md:aspect-square"
          />
        </div>

        <div className="flex flex-col p-6 md:p-8">
          <div className="mb-4 flex items-center gap-3 border-b border-[#D4AF37]/20 pb-4">
            <div className="gold-ring w-[38px]">
              <div className="overflow-hidden rounded-full bg-[#4A0E1F]">
                <div className="flex aspect-square items-center justify-center font-serif italic text-[#F5D97A]">
                  f
                </div>
              </div>
            </div>
            <div>
              <div className="font-body text-[13px] text-[#FDFBF7]">@fairylights.uae</div>
              <div className="flex items-center gap-1 font-body text-[11px] text-[#FDFBF7]/60">
                <MapPin className="h-3 w-3" /> {post.location}
              </div>
            </div>
          </div>

          <p className="font-serif text-[22px] leading-[1.35] tracking-tight text-[#FDFBF7]">
            {post.caption}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {post.hashtags.map((t) => (
              <span
                key={t}
                className="font-body text-[12px] tracking-wide text-[#D4AF37]"
              >
                {t}
              </span>
            ))}
          </div>

          <div className="mt-auto border-t border-[#D4AF37]/20 pt-4">
            <div className="mb-3 flex items-center gap-5">
              <button
                onClick={() => setLiked((v) => !v)}
                className="transition-transform hover:scale-110"
                aria-label="Like"
              >
                <Heart
                  className={`h-5 w-5 transition-colors ${
                    liked ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#FDFBF7]"
                  }`}
                />
              </button>
              <MessageCircle className="h-5 w-5 text-[#FDFBF7]" />
              <Send className="h-5 w-5 text-[#FDFBF7]" />
              <button
                onClick={() => setSaved((v) => !v)}
                className="ml-auto transition-transform hover:scale-110"
                aria-label="Save"
              >
                <Bookmark
                  className={`h-5 w-5 transition-colors ${
                    saved ? "fill-[#D4AF37] text-[#D4AF37]" : "text-[#FDFBF7]"
                  }`}
                />
              </button>
            </div>
            <div className="font-body text-[13px] text-[#FDFBF7]">
              <span className="text-[#D4AF37]">{formatK(post.likes + (liked ? 1 : 0))}</span> likes
            </div>
            <div className="mt-1 font-body text-[11px] uppercase tracking-[0.24em] text-[#FDFBF7]/50">
              View all {post.comments} comments
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
